import { Component, inject, Injector, OnInit, signal } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import {
  MatSnackBar,
  MatSnackBarHorizontalPosition,
  MatSnackBarVerticalPosition,
} from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Observable, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';
import { QueryRef } from 'apollo-angular';
import {
  BasisPrice,
  CashPrice,
  Lot,
  MarketType,
  PriceType,
  PriceUnion,
} from 'src/app/components/bids-and-offers/lots.model';
import { LotDetailsService } from 'src/app/services/lot-details.service';
import { NegotiationActionsService } from 'src/app/services/negotiation-actions.service';
import { UserService } from 'src/app/services/user.service';
import { NegotiationDetailsDialogComponent } from '../../custom/negotiation-details-dialog/negotiation-details-dialog.component';
import { UserDetails, UserRoles } from '../../login/user.model';
import { LotDataService } from 'src/app/services/lot-data.service';
import { LotsService } from 'src/app/services/lots.service';
import { CommonModule, Location } from '@angular/common';
import { NegotiationsService } from 'src/app/services/negotiations.service';
import { faHandshake, faEdit } from '@fortawesome/free-regular-svg-icons';
import {
  DialogType,
  LotActionDialogTemplateComponent,
} from '../desktop-view/negotiation-details-card/lot-action-dialog-template/lot-action-dialog-template.component';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UserState } from 'src/app/store/user/user.reducer';
import { LotState } from 'src/app/store/lots-library/lots.reducer';
import { Store } from '@ngrx/store';
import { Negotiation } from '../../negotiations/negotiations.model';
import { TableDataState } from 'src/app/store/table-data/table-data.reducer';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { PriceTypePipe } from 'src/app/pipes/price-type.pipe';
import { selectTableData } from 'src/app/store/table-data/table-data.selectors';
import { selectUser } from 'src/app/store/user/user.selectors';
import { JCTradingIndicationComponent } from '../../custom/chips/jc-trading-indication/jc-trading-indication.component';
import { LotDetailsResponse } from 'src/app/components/bids-and-offers/lots.model';

@Component({
  selector: 'app-lot-details',
  templateUrl: './lot-details.component.html',
  styleUrls: ['./lot-details.component.css'],
  standalone: true,
  imports: [
    PriceTypePipe,
    CommonModule,
    RouterModule,
    MatCardModule,
    MatChipsModule,
    MatCheckboxModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatTableModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    FontAwesomeModule,
    JCTradingIndicationComponent,
  ],
})
export class LotDetailsComponent implements OnInit {
  private analytics = inject(Analytics);
  faHandShake = faHandshake;
  faEdit = faEdit;
  id: string;
  lot$: Observable<Lot>;
  market$: Observable<MarketType>;
  lotData = signal<Lot>(null);
  lotPriceType = signal<PriceType>(null);
  lotId: string;

  lotDetailsQueryRef: QueryRef<LotDetailsResponse>;
  negotiating = signal(false);
  negotiations = signal<Negotiation[]>(null);
  canNegotiateAndAccept = signal(false);

  counterForm = this.fb.group({
    // originator_id: [''],
    lot_id: [''],
    price_type: [''],
    cash_price: this.fb.group({
      amount: [0],
    }),
    basis_price: this.fb.group({
      basisPoints: [0, Validators.required],
      month: ['', Validators.required],
      product_code: ['', Validators.required],
      year: [0, Validators.required],
    }),
    volume_value: [0],
    ship_begin: [''],
    ship_end: [''],
  });

  user = signal<UserDetails>(null);
  isLoading: boolean;
  sameYear: boolean;
  userDetails$: Observable<UserDetails>;
  dialogConfig = new MatDialogConfig();
  horizontalPosition: MatSnackBarHorizontalPosition = 'center';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private lotDetailsService: LotDetailsService,
    private lotDataService: LotDataService,
    private injector: Injector,
    private fb: FormBuilder,
    private store: Store<{
      user: UserState;
      activeLot: LotState;
      lotTableData: TableDataState;
    }>,
    private lotsService: LotsService,
    private negotiationsService: NegotiationsService,
    private negotiationsActionsService: NegotiationActionsService,
    private userService: UserService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.dialogConfig.disableClose = true;
  }

  ngOnInit() {
    this.market$ = this.store
      .select(selectTableData)
      .pipe(map((res) => res.selectedMarket));

    this.userDetails$ = this.store.select(selectUser).pipe(
      map((user) => {
        if (user) {
          this.canNegotiateAndAccept.set(user.role !== UserRoles.Farmer);
        }
        return user;
      })
    );
    this.id = this.route.snapshot.paramMap.get('id');

    // Now, you can use this.id to fetch and display the details
    this.isLoading = true;
    /** lotDetails service query also returns all active negotiations of the user
      if negotions list contains lot_id of current lot, hide counter btn and show handshake icon
    **/
    this.lotDetailsQueryRef = this.lotDetailsService.watch({
      itemId: this.id,
    });
    this.lot$ = this.lotDetailsQueryRef.valueChanges.pipe(
      map((result) => {
        logEvent(this.analytics, LogEventsEnum.lots_detail_view);
        this.isLoading = false;
        this.lotData.set(result.data.lot);
        this.lotPriceType.set(
          this.lotsService.isCashPrice(result.data.lot.price as PriceUnion)
            ? PriceType.Cash
            : PriceType.Basis
        );
        this.counterForm.patchValue(
          {
            lot_id: this.lotData()._id,
            price_type: this.lotPriceType(),
            ...(this.lotPriceType() === PriceType.Cash
              ? {
                  cash_price: { ...(result.data.lot.price as CashPrice) },
                  basis_price: null,
                }
              : {
                  basis_price: { ...(result.data.lot.price as BasisPrice) },
                  cash_price: null,
                }),
            volume_value: this.lotData().volume_value || 0,
            ship_begin: this.lotData().ship_begin || '',
            ship_end: this.lotData().ship_end || '',
          },
          { emitEvent: false }
        );
        this.sameYear = this.isSameYear(result.data.lot);
        this.lotId = result.data.lot._id;
        return result.data;
      }),
      concatMap((data) => {
        const negs = data.negotiations.filter(
          (n) => n.lot_id === data.lot._id && n.status === 'Active'
        );
        this.negotiating.set(negs.length > 0);
        if (this.negotiating()) {
          this.negotiations.set(negs);
        }
        return of(data.lot);
      })
    );
  }

  editLot() {
    // this.isEditing.update((state) => !state);
    const counterDialogRef = this.dialog.open(
      LotActionDialogTemplateComponent,
      {
        injector: this.injector,
        data: {
          form: this.counterForm,
          lotId: this.lotId,
          dialogTitle: `Edit ${this.lotData().lot_type}`,
          dialogType: DialogType.Edit,
        },
      }
    );

    counterDialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result.confirm) {
          logEvent(this.analytics, LogEventsEnum.lot_edit);
          this.counterForm.patchValue({ ...result.data });
          let values = this.counterForm.value;
          values = this.lotsService.removeProperty(
            values,
            values.price_type === PriceType.Basis ? 'cash_price' : 'basis_price'
          );
          values = this.lotsService.removeProperty(values, 'lot_id');

          this.lotDataService.editLot(this.lotId, values).subscribe({
            next: (result) => {
              // console.log('new nego response', result.data);
              this.counterForm.setErrors({ sameValues: false });
              this.snackBar.open(
                `Updated ${this.lotData().lot_type}`,
                'Close',
                {
                  duration: 3500,
                  panelClass: ['blue-snackbar'],
                }
              );
              this.lotDetailsQueryRef.refetch();
            },
            error: (err) => {
              console.error('Error creating negotiation:', err.message);
              this.snackBar.open(`Error creating negotiation`, 'Close', {
                duration: 3500,
                panelClass: ['blue-snackbar'],
              });
            },
          });
        }
      },
    });
  }

  accept() {
    this.dialogConfig.data = {
      dialogText: 'Are you sure you want to accept?',
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );
    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log("contract dialog close result", result);
        if (result.confirm) {
          logEvent(this.analytics, LogEventsEnum.lots_detail_accept_tap);
          this.negotiationsActionsService
            .createContract({
              lot_id: this.lotId,
            })
            .subscribe({
              next: (result) => {
                // console.log("contract creation result", result)
                this.snackBar.open(
                  `${this.lotData().lot_type} Accepted`,
                  null,
                  {
                    horizontalPosition: this.horizontalPosition,
                    verticalPosition: this.verticalPosition,
                    duration: 3000,
                    panelClass: 'blue-snackbar',
                  }
                );
                this.router.navigate(['app/trades']);
              },
              error: (err) => {
                // console.log("contract creation error", err)
                this.snackBar.open('Error accepting negotiation', null, {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 2000,
                });
              },
            });
        }
      },
    });
  }

  repost(lotId): void {
    logEvent(this.analytics, LogEventsEnum.lots_detail_repost_tap);
    this.dialogConfig.data = {
      dialogText: `Are you sure you want to Repost this ${
        this.lotData().lot_type
      }?`,
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log("contract dialog close result", result);
        if (result.confirm) {
          // console.log(
          //   `confirm repost. lots/new/${this.lotType.toLowerCase()}`,
          //   this.lotData()
          // );
          // go to new lot form with select lot data in form
          this.router
            .navigate([`app/lots/repost`, lotId], {
              state: { lotData: this.lotData() },
            })
            .catch(() => {
              this.snackBar.open(
                `Error reposting ${this.lotData().lot_type}`,
                'close',
                {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  duration: 2000,
                }
              );
            });
        }
      },
    });
  }

  revoke(lotId: string) {
    this.dialogConfig.data = {
      dialogText: `Are you sure you want to remove ${this.lotData().lot_type}?`,
    };
    const dialogRef = this.dialog.open(
      NegotiationDetailsDialogComponent,
      this.dialogConfig
    );

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        // console.log("contract dialog close result", result);
        if (result.confirm) {
          logEvent(this.analytics, LogEventsEnum.lots_detail_withdraw_tap);
          this.lotDataService.removeLot(lotId).subscribe({
            next: (result) => {
              // console.log("remove lot result", result)
              this.snackBar.open(
                `${this.lotData().lot_type} removed`,
                'close',
                {
                  horizontalPosition: this.horizontalPosition,
                  verticalPosition: this.verticalPosition,
                  panelClass: ['blue-snackbar'],
                }
              );
              this.location.back();
            },
            error: (err) => {
              // console.log("contract creation error", err)
              this.snackBar.open('Error removing bid/offer', null, {
                horizontalPosition: this.horizontalPosition,
                verticalPosition: this.verticalPosition,
                duration: 2000,
              });
            },
          });
        }
      },
    });
  }

  //create counter if it has no negotiation
  counter() {
    logEvent(this.analytics, LogEventsEnum.lots_detail_counter_tap);
    //open dialog counter
    const counterDialogRef = this.dialog.open(
      LotActionDialogTemplateComponent,
      {
        injector: this.injector,
        data: {
          form: this.counterForm,
          lotId: this.lotId,
          dialogTitle: 'Counter',
          dialogType: DialogType.Counter,
        },
      }
    );

    counterDialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result.confirm) {
          logEvent(this.analytics, LogEventsEnum.lots_detail_counter_tap);
          this.counterForm.patchValue({ ...result.data });
          let values = this.counterForm.value;
          values = this.lotsService.removeProperty(
            values,
            values.price_type === PriceType.Basis ? 'cash_price' : 'basis_price'
          );
          this.negotiationsActionsService
            .createNegotiation({
              ...values,
              lot_id: this.lotData()._id,
            })
            .subscribe({
              next: (result) => {
                // console.log('new nego response', result.data);
                this.counterForm.setErrors({ sameValues: false });
                this.snackBar.open(`Created Negotiation`, 'Close', {
                  duration: 3500,
                  panelClass: ['blue-snackbar'],
                });
                this.lotDetailsQueryRef.refetch();
              },
              error: (err) => {
                console.error('Error creating negotiation:', err.message);
                this.snackBar.open(`Error creating negotiation`, 'Close', {
                  duration: 3500,
                  panelClass: ['blue-snackbar'],
                });
              },
            });
        }
      },
    });
  }

  isSameYear(lot: Lot): boolean {
    return (
      new Date(lot.ship_begin).getFullYear() ===
      new Date(lot.ship_end).getFullYear()
    );
  }

  // formatProductType(lot: BidAndOffer): string {
  //   // Assuming grade is also a property of the lot object
  //   return lot.product_type.replace(lot.grade, '').trim();
  // }
}
